<template>
  <div class="relative terminal-min-height q-pa-xs">
    <transition-group name="terminal-item">
      <div
        v-for="(item, i) in data.objects"
        :key="`${item.type}:${i}`"
        class="q-mb-sm"
      >
        <component
          :is="$dynamicComponents[item.type]"
          v-bind="{ data: item.data, rawData: item }"
          @click="handleClick"
        />
      </div>
    </transition-group>

    <confirm-modal
      ref="confirmModal"
    />

    <offer-modal
      ref="offerModal"
    />

    <portal to="settings">
      <tiles :schema="tilesSchema" />
    </portal>

    <dynamic-counter-modal
      ref="dynamicCounterModal"
      @click="handleClick"
    />

    <error-screen
      :message="error"
      @reset="handleErrorClose"
    />
  </div>
</template>

<script>
// Vuex
import { mapMutations } from 'vuex'

export default {
  name: 'SortingContent',
  emits: ['reset', 'block', 'unblock'],
  props: {
    barcode: {
      type: Object,
      default () {
        return {}
      }
    },
    queues: {
      type: Array,
      default () {
        return []
      }
    },
    place: {
      type: [String, Number],
      default () {
        return null
      }
    }
  },
  data () {
    return {
      queue: null,
      sequence: null,
      quantity: 1,
      numberOfScanningItems: 0,
      objects: {},
      data: {
        objects: [
          ...this.queues,
          {
            type: 'message',
            data: {
              position: 'bottom',
              text: this.$t('Choose queue')
            }
          }
        ]
      },
      error: ''
    }
  },
  computed: {
    tilesSchema () {
      return [
        {
          id: 1,
          label: this.$t('Reset'),
          icon: 'refresh',
          value: true,
          onChanged: () => {
            this.handleReset()
          }
        },
        {
          id: 2,
          label: this.$t('Choose Queue'),
          icon: 'grading',
          value: true,
          onChanged: () => {
            this.handleBackToQueues()
          }
        },
        {
          id: 3,
          label: this.$t('Skip Task'),
          icon: 'skip_next',
          value: true,
          onChanged: () => {
            this.handleClick({ event: 'storage.queue.task.reject' })
          }
        }
      ]
    }
  },
  watch: {
    barcode (newVal) {
      this.handleBarcode(newVal)
    }
  },
  methods: {
    ...mapMutations([
      'addErrorNotification'
    ]),
    handleBack () {
      this.$router.push('/terminal')
    },
    handleReset () {
      this.$emit('reset')
    },
    handleBackToQueues () {
      this.objects = {}
      this.data = {
        objects: [
          ...this.queues,
          {
            type: 'message',
            data: {
              position: 'bottom',
              text: this.$t('Choose queue')
            }
          }
        ]
      }
    },
    handleErrorClose () {
      this.error = ''
    },
    handleClick (item) {
      if (!item.event) {
        return
      }

      const events = {
        focus: () => {
          this.$emit('block')
        },
        focusOut: () => {
          this.$emit('unblock')
        },
        quantityChange: () => {
          this.quantity = item.data.quantity
        },
        counterSubmit: () => {
          this.quantity = item.data.quantity
          this.handleBarcode({ raw: item.data.barcode, value: item.data.barcode })
        },
        'storage.tasks.queue.opened': () => {
          this.queue = item.data
          this.data.entityClass = item.type
          this.data.event = item.event
          this.sequence = item.sequence

          this.handleBarcode({ value: item.data.id, raw: item.data.id, type: '' })
        },
        'storage.tasks.task.offer.opened': () => {
          const barcode = item.barcodes
            ? item.barcodes[0]
            : item.sku

          this.handleBarcode({ value: barcode, raw: barcode, type: '' })
        },
        'storage.queue.task.reject': () => {
          this.data.event = 'storage.queue.task.reject'
          this.data.entityClass = this.$entities.Orderadmin_Storage_Entity_Picking_Task

          this.handleBarcode({ value: '', raw: '', type: '' })
        },
        'storage.document.opened': () => {
          this.data.event = item.event
          this.data.entityClass = item.entityClass

          this.handleBarcode({ value: item.data.id, raw: item.data.id, type: '' })
        },
        'storage.place.opened': () => {
          this.data.event = item.event
          this.data.entityClass = item.entityClass
          const value = `S/P/${item.data.id}*`
          this.handleBarcode({ value: value, raw: value, type: '' })
        },
        'storage.tasks.task.batch.opened': () => {
          this.data.batch = item.data
          this.handleBarcode(this.barcode)
        }
      }

      if (events[item.event]) {
        return events[item.event]()
      }

      if (item.type) {
        this.data.entityClass = item.type
      }

      this.data.event = item.event
      const value = (item.data && item.data.id) || ''
      this.handleBarcode({ value, raw: value, type: '' })
    },
    handleBarcode (barcode) {
      if (this.data.requiredData && this.data.requiredDataType) {
        const validations = {
          regexp: (value, validation) => {
            const regex = new RegExp(validation)
            return !regex.test(value.raw)
          },
          text: (value, validation) => {
            return value.raw !== validation
          }
        }

        if (!validations[this.data.requiredDataType]) {
          return this.addErrorNotification(this.$t('Validation is not recognized!'))
        }

        if (validations[this.data.requiredDataType](barcode, this.data.requiredData)) {
          return this.addErrorNotification('Barcode does not match scheme!')
        }
      }

      const data = this.reduceData(
        [
          'objects',
          'queue',
          'requiredData',
          'requiredDataType',
          'maxCount',
          'entity',
          'timestamp'
        ],
        {
          timestamp: new Date().toISOString(),
          basket: this.place,
          entity: barcode.raw
        }
      )

      if (this.sequence && !this.data.sequence) {
        data.sequence = this.sequence.id
      }

      if (this.data.event === 'storage.tasks.task.offer.opened' || this.data.event === 'storage.tasks.scan.item') {
        data.quantity = this.quantity
      }

      const task = this.objects[this.$entities.Orderadmin_Storage_Entity_Picking_Task]
        ? { ...this.objects[this.$entities.Orderadmin_Storage_Entity_Picking_Task] }
        : null

      if (!data.task && task) {
        data.task = task.data.id
      }

      if (this.objects[this.$entities.Orderadmin_Storage_Entity_Item]) {
        data.item = this.objects[this.$entities.Orderadmin_Storage_Entity_Item].data.id
      }

      const place = this.getPlaceObject()

      if (place) {
        data.place = place.data.id
      }

      if (this.objects[this.$entities.Orderadmin_Storage_Entity_Movement_Acceptance]) {
        data.document = this.objects[this.$entities.Orderadmin_Storage_Entity_Movement_Acceptance].data.id
      }

      if (this.objects[this.$entities.Barcode]) {
        data.barcode = this.objects[this.$entities.Barcode].data
      }

      if (this.objects[this.$entities.Batch] && !Array.isArray(this.objects[this.$entities.Batch].data)) {
        data.batch = this.objects[this.$entities.Batch].data
      }

      // const productsCollection = this.objects[this.getCollectionName(this.$entities.Orderadmin_Products_Entity_Product_Offer)]

      return this.handleSubmit(barcode, data)
      // return Promise.resolve(!!productsCollection)
      //   .then(hasProductsCollection => {
      //     return hasProductsCollection
      //       ? this.resolveProduct(barcode.raw, productsCollection.data)
      //       : true
      //   })
      //   .then(isOk => {
      //     if (isOk) {
      //       return this.handleSubmit(barcode, data)
      //     }

      //     return null
      //   })
    },
    getPlaceObject () {
      const entity = this.$entities.placeObjects.find(x => this.objects[x])
      return this.objects[entity]
    },
    getPlaceCollection () {
      const entity = this.$entities.placeObjects.find(x => this.objects[this.getCollectionName(x)])
      return this.objects[this.getCollectionName(entity)]
    },
    getShop (collection) {
      return ((collection[0] || {})._embedded || {}).shop
    },
    getOwner (acceptance) {
      if (!acceptance._embedded || !acceptance._embedded.warehouse || !acceptance._embedded.warehouse._embedded || !acceptance._embedded.warehouse._embedded.owner) {
        return null
      }

      return acceptance._embedded.warehouse._embedded.owner._links.self.href.split('/').pop()
    },
    resolveProduct (barcode, collection) {
      this.$emit('block')

      const product = collection.find(x => `${x.sku}`.trim() === barcode)

      if (product) {
        this.$emit('unblock')
        return Promise.resolve(product)
      }

      const options = {
        description: this.$t('Product is not found! Do you want to create new one?'),
        ok: this.$t('Yes'),
        cancel: this.$t('No')
      }

      const shop = this.getShop(collection)
      const owner = this.getOwner(this.objects[this.$entities.Orderadmin_Storage_Entity_Movement_Acceptance].data)

      return this.$refs.confirmModal.show(options)
        .then(isOk => {
          if (isOk) {
            const data = {
              type: 'grouped',
              shop,
              sku: barcode,
              barcodes: [barcode]
            }

            return this.$refs.offerModal.show(data, { owner })
          }

          return isOk
        })
        .then(result => {
          this.$emit('unblock')
          return result
        })
    },
    handleSubmit (barcode, data) {
      return this.$service.picking.process(this.queue.id, data)
        .then(data => {
          this.objects = data.objects.reduce((acc, obj) => {
            const key = obj.type === 'collection'
              ? this.getCollectionName(obj.entityClass)
              : obj.type

            acc[key] = obj

            return acc
          }, {})

          this.data = data
          this.quantity = 1

          if (data.event === 'storage.tasks.task.offer.opened') {
            this.numberOfScanningItems += 1

            if (this.numberOfScanningItems > 1) {
              const taskColName = this.getCollectionName(this.$entities.Orderadmin_Products_Entity_Product_Offer)

              if (
                this.objects[taskColName] &&
                this.objects[taskColName].options &&
                this.objects[taskColName].options.counter
              ) {
                this.$refs.dynamicCounterModal.open(this.objects[taskColName].data, barcode.raw, 'offers')
              }
            }
          } else {
            this.numberOfScanningItems = 0
          }
        })
        .catch(err => {
          this.error = err.message
        })
    },
    getCollectionName (entity) {
      return `collection:${entity}`
    },
    reduceData (ignoredKeys = [], defaultValue = {}) {
      return Object.keys(this.data)
        .filter(key => !ignoredKeys.includes(key))
        .reduce((acc, key) => {
          if (!this.data[key]) {
            return acc
          }

          return {
            ...acc,
            [key]: typeof this.data[key] === 'object' && !Array.isArray(this.data[key])
              ? this.data[key].id
              : this.data[key]
          }
        }, { ...defaultValue })
    }
  }
}
</script>
